/**
 * Wizard step 4 — preview the trade before committing.
 *
 * Renders the response of POST /api/bot/preview: shares to buy, share cost,
 * premium collected, net out-of-pocket. The "Confirm" action lives in the
 * parent Wizard.
 */

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

export interface BotPreview {
  strategy?: string;
  symbol: string;
  currentPrice: number;
  existingShares?: number;
  sharesTarget?: number;
  sharesNeeded?: number;
  shareCost?: number;
  contracts: number;
  expiration: string;
  strike?: number;
  premiumPerShare?: number;
  premiumTotal?: number;
  netCost?: number;
  optionSymbol?: string;
  // Iron Condor fields
  putBuyStrike?: number;
  putSellStrike?: number;
  callSellStrike?: number;
  callBuyStrike?: number;
  putCredit?: number;
  callCredit?: number;
  netCreditPerShare?: number;
  totalCredit?: number;
  spreadWidth?: number;
  maxLossPerContract?: number;
  totalMaxLoss?: number;
  marginRequired?: number;
  profitTarget50pct?: number;
  returnOnRisk?: number;
}

interface Props {
  preview: BotPreview | null;
  loading?: boolean;
  error?: string | null;
}

const fmtMoney = (n: number | null | undefined): string =>
  n === null || n === undefined || Number.isNaN(n)
    ? '—'
    : `$${n.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

const Row: React.FC<{ label: string; value: React.ReactNode; bold?: boolean }> = ({
  label,
  value,
  bold,
}) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: bold ? 700 : 500 }}>
      {value}
    </Typography>
  </Box>
);

const PreviewSummary: React.FC<Props> = ({ preview, loading, error }) => {
  if (loading) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Computing preview…
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (!preview) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Pick a strike to see the preview.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (preview.strategy === 'IRON_CONDOR') {
    return (
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="overline" color="text.secondary">
                IRON CONDOR
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {preview.contracts}× {preview.symbol} Iron Condor
              </Typography>
              <Typography variant="caption" color="text.secondary">
                expires {preview.expiration}
              </Typography>
            </Box>

            <Divider />

            <Box>
              <Row label="Current price" value={fmtMoney(preview.currentPrice)} />
              <Row label="Expiration" value={preview.expiration} bold />
              <Row
                label="Put spread"
                value={`$${preview.putBuyStrike?.toFixed(2)} / $${preview.putSellStrike?.toFixed(2)}`}
              />
              <Row
                label="Call spread"
                value={`$${preview.callSellStrike?.toFixed(2)} / $${preview.callBuyStrike?.toFixed(2)}`}
              />
              <Row label="Spread width" value={fmtMoney(preview.spreadWidth)} />
              <Row label="Contracts" value={preview.contracts.toString()} />
            </Box>

            <Divider />

            <Box>
              <Row label="Put credit" value={fmtMoney(preview.putCredit)} />
              <Row label="Call credit" value={fmtMoney(preview.callCredit)} />
              <Row
                label={`Net credit / share (${preview.contracts} contract${preview.contracts > 1 ? 's' : ''})`}
                value={fmtMoney(preview.netCreditPerShare)}
              />
              <Row
                label="Total credit received"
                value={
                  <Box component="span" sx={{ color: 'success.main', fontWeight: 700 }}>
                    +{fmtMoney(preview.totalCredit)}
                  </Box>
                }
                bold
              />
            </Box>

            <Divider />

            <Box>
              <Row
                label="Margin required (max loss)"
                value={fmtMoney(preview.marginRequired)}
                bold
              />
              <Row
                label="Profit target (50%)"
                value={
                  <Box component="span" sx={{ color: 'success.main' }}>
                    {fmtMoney(preview.profitTarget50pct)}
                  </Box>
                }
              />
              <Row
                label="Return on risk"
                value={`${preview.returnOnRisk?.toFixed(1)}%`}
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  const profitIfAssigned =
    ((preview.strike || 0) - preview.currentPrice) * preview.contracts * 100 +
    (preview.premiumTotal || 0);

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="overline" color="text.secondary">
              SETUP
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {preview.contracts}× {preview.symbol} call @ {fmtMoney(preview.strike)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              expires {preview.expiration} · {preview.optionSymbol}
            </Typography>
          </Box>

          <Divider />

          <Box>
            <Row label="Current price" value={fmtMoney(preview.currentPrice)} />
            <Row
              label="Shares already held"
              value={(preview.existingShares || 0).toLocaleString()}
            />
            <Row
              label="Shares to buy"
              value={(preview.sharesNeeded || 0).toLocaleString()}
            />
            <Row label="Share cost" value={fmtMoney(preview.shareCost)} />
          </Box>

          <Divider />

          <Box>
            <Row
              label={`Premium / share (${preview.contracts} contract${preview.contracts > 1 ? 's' : ''})`}
              value={fmtMoney(preview.premiumPerShare)}
            />
            <Row
              label="Total premium collected"
              value={
                <Box component="span" sx={{ color: 'success.main' }}>
                  +{fmtMoney(preview.premiumTotal)}
                </Box>
              }
            />
          </Box>

          <Divider />

          <Box>
            <Row
              label="Net out-of-pocket"
              value={fmtMoney(preview.netCost)}
              bold
            />
            <Row
              label="Max profit if assigned at expiry"
              value={
                <Box component="span" sx={{ color: 'success.main' }}>
                  {fmtMoney(profitIfAssigned)}
                </Box>
              }
            />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default PreviewSummary;
